import { useCallback, type HTMLInputAutoCompleteAttribute } from 'react';
import { useRouter } from 'next/router';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CompanySizesOptions,
  ContactFormFields,
  ContactFormMetadata,
  PartnerTypesOptions,
  type ContactFormFields as TContactFormFields,
} from '@transcend-io/contact-form-schema';
import type { TrackingEvent } from '@transcend-io/segment/core';
import { useForm, type ControllerRenderProps } from 'react-hook-form';
import type { Schema } from 'zod';

import { isPersonalEmail } from '#/lib/contact-form-client/personal-email';
import { getCookie } from '#/lib/cookies';
import type {
  SanityDefaultForm,
  SanityFormField,
  SanityFormFieldEmail,
  SanityMultiForm,
} from '#/lib/sanity/queries/form';
import { useAnalytics } from '#/lib/segment';
import { conditionalTrackLinkedInConversion } from '#/lib/segment/linkedin-insight';
import { Input } from '#/components/Input';
import { Listbox } from '#/components/Listbox';
import { Textarea } from '#/components/Textarea';

function getAutoCompleteAttribute(
  fieldName: keyof typeof TContactFormFields.shape,
): HTMLInputAutoCompleteAttribute {
  switch (fieldName) {
    case 'company':
      return 'organization';
    case 'title':
      return 'organization-title';
    case 'firstName':
      return 'given-name';
    case 'lastName':
      return 'family-name';

    default:
      return '';
  }
}

export function getFormFieldComponent(
  fieldData: SanityFormField,
  fieldProps: ControllerRenderProps,
  autoFocus?: boolean,
) {
  switch (fieldData._type) {
    case 'text':
      return (
        <Input
          type={fieldData._type}
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          autoComplete={getAutoCompleteAttribute(
            fieldProps.name as keyof typeof TContactFormFields.shape,
          )}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'email':
      return (
        <Input
          type={fieldData._type}
          autoComplete="work email"
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'tel':
      return (
        <Input
          type={fieldData._type}
          autoComplete="work tel"
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'textarea':
      return (
        <Textarea
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          autoComplete={getAutoCompleteAttribute(
            fieldProps.name as keyof typeof TContactFormFields.shape,
          )}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'select': {
      let options;
      if (fieldData.name === 'partnerType') {
        options = PartnerTypesOptions;
      } else if (fieldData.name === 'companySize') {
        options = CompanySizesOptions;
      }
      return (
        <Listbox
          {...fieldProps}
          options={
            options?.map((option) => ({
              label: option,
              value: option,
            })) ?? []
          }
          placeholder={fieldData.label}
        />
      );
    }
    default:
      return null;
  }
}
// If the email field requires a business address, we need to add a validation rule to the form schema
export function addBusinessEmailValidation(
  form: SanityDefaultForm | SanityMultiForm,
) {
  // Determine the correct field array based on the form type
  const allFields =
    form.formType === 'default'
      ? form.fields
      : form.fieldGroups.flatMap((group) => group.fields);

  // Find the email field within the resolved fields array
  const emailField = allFields.find(
    (field): field is SanityFormFieldEmail =>
      'name' in field && field.name === 'email',
  );

  // Apply business email validation if required
  const formFieldsSchema = emailField?.requireBusinessEmail
    ? ContactFormFields.extend({
        // Validate that the email address is not a personal email
        email: ContactFormFields.shape.email.refine(
          (value) => !isPersonalEmail(value),
          {
            message: 'Please enter a work email address.',
          },
        ),
      })
    : ContactFormFields;

  return formFieldsSchema;
}

interface UseFormMetadataArgs {
  pardotCampaignId: SanityDefaultForm['pardotCampaignId'];
  pardotListIds: SanityDefaultForm['pardotListIds'];
  formFieldsSchema: Schema<TContactFormFields>;
}

export const useFormMetadata = (data: UseFormMetadataArgs) => {
  const {
    query: {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_id,
      utm_term,
      utm_content,
    },
  } = useRouter();

  const concatPardotListIds = data.pardotListIds
    ?.filter(Boolean)
    .map(String)
    .join(', ');

  const metadata: ContactFormMetadata = {
    pardotCampaignId: data.pardotCampaignId,
    pardotListIds: concatPardotListIds,
    utm_source: typeof utm_source === 'string' ? utm_source : undefined,
    utm_medium: typeof utm_medium === 'string' ? utm_medium : undefined,
    utm_campaign: typeof utm_campaign === 'string' ? utm_campaign : undefined,
    utm_id: typeof utm_id === 'string' ? utm_id : undefined,
    utm_term: typeof utm_term === 'string' ? utm_term : undefined,
    utm_content: typeof utm_content === 'string' ? utm_content : undefined,
  };

  const methods = useForm<ContactFormFields>({
    resolver: (values, context, options) => {
      if ('companySize' in values && !values.companySize) {
        values.companySize = undefined;
      }

      return zodResolver(data.formFieldsSchema)(values, context, options);
    },
  });

  const getMetadata = (recaptchaToken?: string) => {
    const koalaId = getCookie('ko_id');
    return { ...metadata, recaptchaToken, koalaId };
  };

  return { methods, getMetadata };
};
